import { BaseText, Headline3, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useDurationFromDate from 'hooks/useDurationFromDate';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import { country, deviceLanguage } from 'states/persistInStorage';
import styled from 'styled-components/native';
import { CenterRow, ColumnWithMargins } from 'styles/ContainerStyles';
import { Maybe, ParkingSession } from 'types/generatedSchemaTypes';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getPaymentMethodDetails } from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getTimeFromDate, getTodayYesterdayOrDate } from 'utils/timeUtils';
import {
  permitCoversParking,
  SessionPriceInfo,
} from './OngoingParkingComponents';
import { StatusContainer, StyledMeta, Time } from './styles';
import { useUiStore } from 'stores/uiStore';

const IconWrapperForParking = styled.View`
  align-items: center;
  justify-content: center;
  max-width: 300px;
  width: 100%;
`;

const SmallerText = styled(Headline3)<{
  ml?: number;
  mt?: number;
}>`
  margin-top: ${({ mt }) => (mt ? mt + 'px' : '4px')};
  margin-left: ${({ ml }) => (ml ? ml + 'px' : '0px')};
  color: ${({ theme: t }) => t.colors.white};
`;

type AnprPaymentInfoProps = {
  permitCoversParking: boolean | undefined;
  paymentMethodLogo: { logoName: string; logoSize: string } | undefined;
  paymentMethodInfo: Maybe<string> | undefined;
};

const AnprPaymentInfo: React.FC<AnprPaymentInfoProps> = ({
  permitCoversParking,
  paymentMethodLogo,
  paymentMethodInfo,
}) => {
  const { t } = useTranslation();

  if (!permitCoversParking) {
    return (
      <ColumnWithMargins {...testIdentifiers('anpr-payment-method')} mt={10}>
        <IconWrapperForParking>
          {paymentMethodLogo && (
            <Icon
              name={`${paymentMethodLogo?.logoName}`}
              size={`${paymentMethodLogo?.logoSize}`}
              color={theme.colors.white}
            />
          )}
          <StyledMeta ml={5}>{paymentMethodInfo}</StyledMeta>
        </IconWrapperForParking>
      </ColumnWithMargins>
    );
  }
  return (
    <CenterRow isVerticalCenter>
      <View style={{ marginTop: 2 }}>
        <Icon size="m" name="Services-Outlined" color={theme.colors.white} />
      </View>
      <StyledMeta center {...testIdentifiers('anpr-description')} ml={5}>
        {t('parking.anprCoveredByPermit')}
      </StyledMeta>
    </CenterRow>
  );
};

const OngoingAnprParking = (session: ParkingSession) => {
  const { t } = useTranslation();
  const {
    customer: { paymentCards, companyBenefits },
  } = useContext(AccountContext);

  const isSessionCoveredByPermit = permitCoversParking(session);

  const startDate = new Date(session?.startDate as Date);
  const duration = useDurationFromDate(startDate, 30000, [
    'days',
    'hours',
    'minutes',
  ]);
  const parkingCountry = session.countryCode;
  const parkingStartedTime = getTodayYesterdayOrDate(
    startDate,
    deviceLanguage()
  )
    ? getTodayYesterdayOrDate(startDate, deviceLanguage()) +
      ' ' +
      getTimeFromDate(startDate, parkingCountry ?? country())
    : getTimeFromDate(startDate, parkingCountry ?? country());

  const { paymentMethodInfo, paymentMethodLogo } = getPaymentMethodDetails(
    session,
    paymentCards,
    companyBenefits
  );

  const { ongoingParkingSession: parkingSession } = useOngoingParkingSession();

  const {
    flags: { allow_manual_stop_anpr },
  } = useContext(FeatureFlagsContext);
  const allowManualStopANPR = showFeatureFlag(allow_manual_stop_anpr);

  return (
    <StatusContainer>
      <ColumnWithMargins mt={10} mb={70}>
        <SmallerText center {...testIdentifiers('anpr-description')}>
          {t('parking.anprDescription')}
        </SmallerText>
      </ColumnWithMargins>
      <ColumnWithMargins>
        <SmallerText {...testIdentifiers('anpr-duration-text')}>
          {t('parking.duration')}
        </SmallerText>
      </ColumnWithMargins>
      <Time {...testIdentifiers('anpr-duration')}>{duration}</Time>
      <ColumnWithMargins mt={10}>
        <SmallerText center {...testIdentifiers('anpr-startTime')}>
          {t('parking.anprStartedTime')} {parkingStartedTime}
        </SmallerText>
      </ColumnWithMargins>
      <SessionPriceInfo session={session} />
      <AnprPaymentInfo
        permitCoversParking={isSessionCoveredByPermit}
        paymentMethodLogo={paymentMethodLogo}
        paymentMethodInfo={paymentMethodInfo}
      />
      {allowManualStopANPR &&
        parkingSession?.parkZone?.parkingMethods?.manualStop && (
          <ColumnWithMargins mt={10}>
            <SmallerText center {...testIdentifiers('anpr-startTime')}>
              {t('parking.anprManualDriveOut')}
            </SmallerText>
            <Pressable
              onPress={() => {
                useUiStore.getState().setSheetModal('stopOngoingParkingAnpr');
              }}>
              <BaseText center>
                <Text
                  style={{
                    textDecorationLine: 'underline',
                    color: '#f1957a',
                  }}
                  {...testIdentifiers('anpr-stop')}>
                  {t('parking.stopParking')}
                </Text>
              </BaseText>
            </Pressable>
          </ColumnWithMargins>
        )}
    </StatusContainer>
  );
};
export default OngoingAnprParking;
